import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { EnvelopeSimple, ArrowLeft, Phone } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const showError = (msg) => {
    setError(msg);
    toast.error(msg);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const value = identifier.trim();
    if (!value) { showError("Ingresa tu correo o teléfono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email_or_phone: value.toLowerCase() });
      setSent(true);
      toast.success("Si el correo o teléfono existe, recibirás un enlace en breve");
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
    } catch (err) {
      showError(err.response?.data?.detail || "Error al procesar solicitud");
    } finally {
      setLoading(false);
    }
  };

  const isPhone = /^[+\d\s()-]+$/.test(identifier.trim()) && identifier.trim().length >= 6;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-3 sm:p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-gray-500 hover:text-[#CCFF00] mb-4 sm:mb-6 transition-colors" data-testid="back-to-login">
          <ArrowLeft size={14} /> Volver al login
        </Link>

        {error && !sent && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-300 px-3 py-2 mb-4 text-xs" data-testid="forgot-error">
            {error}
          </div>
        )}

        <div className="bg-[#141414] border border-white/10 p-4 sm:p-8 relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#CCFF00]" />
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Recuperar acceso</p>
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-6">Restablecer Contraseña</h1>
          {sent ? (
            <div className="space-y-4" data-testid="forgot-sent">
              <p className="text-sm text-gray-300">Si la cuenta existe, te enviamos un enlace para restablecer tu contraseña.</p>
              <p className="text-xs text-gray-500">Revisa tu bandeja de entrada, spam o tu WhatsApp. El enlace expira en 1 hora.</p>
              <Link to="/login" className="inline-flex items-center gap-2 bg-[#CCFF00] text-black px-4 h-10 text-xs font-bold uppercase tracking-wider hover:bg-[#B3E600] transition-colors">
                Ir al login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-400">Correo electrónico o teléfono</Label>
                <Input
                  type="text"
                  autoComplete="username"
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="tu@correo.com o +525551234567"
                  className="bg-[#0A0A0A] border-white/20 text-white h-11 mt-1"
                  data-testid="forgot-email-input"
                />
                <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1">
                  {isPhone ? <><Phone size={12} /> Recibirás un enlace por WhatsApp o SMS</> : <><EnvelopeSimple size={12} /> Recibirás un enlace seguro con validez de 1 hora</>}
                </p>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide" data-testid="forgot-submit">
                {loading ? "Enviando..." : "Enviar enlace"}
              </Button>
              <p className="text-[11px] text-gray-500 text-center">
                También puedes <Link to="/login" className="text-[#CCFF00] hover:underline">iniciar con WhatsApp</Link> sin contraseña.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
